import React from 'react';
import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import { Link as DSLink } from '@hausgold/designsystem';
import OutboundLink from './OutboundLink';
import {
  analyticsTrack,
  EVENT_OBJECT,
  EVENT_VERB,
  eventBuilder,
} from '../utils/tracking';

const EXTERNAL_LINK_REL = ['nofollow', 'noopener', 'noreferrer'];

/**
 * General `Link`
 *
 * All internal links (starting with `/` or matching https://*.hausgold.de`)
 * will be handled by `GatsbyLink`, all system links ('mail:to', 'tel:') are handled as normal links
 * while all others will be treated as external
 * and handled by Google Analytic's `OutboundLink`.
 */
const Link = ({
  children,
  to,
  activeClassName,
  partiallyActive,
  eventName,
  eventLabel,
  eventParams,
  useNativeLink = false,
  doFollow = false,
  ...other
}) => {
  // Get all "Follow links" from Contentful
  const {
    allContentfulFollowLink: { nodes: followLinks },
  } = useStaticQuery(graphql`
    {
      allContentfulFollowLink {
        nodes {
          url
        }
      }
    }
  `);

  const handleClick = (linkTo, additionalParams = {}) => {
    analyticsTrack({
      event:
        eventName || eventBuilder(EVENT_OBJECT.button, EVENT_VERB.interacted),
      params: {
        label: eventLabel || 'Link',
        to: linkTo,
        element_type: 'Link',
        ...eventParams,
        ...additionalParams,
      },
    });
  };

  const isTelephone = to?.startsWith('tel:');
  // Links containing a telephone number. No href rewriting required.
  if (isTelephone) {
    return (
      <DSLink
        onClick={() => {
          handleClick(to, { element_type: 'TelLink' });
        }}
        href={to}
        activeClassName={activeClassName}
        {...other}
      >
        {children}
      </DSLink>
    );
  }

  const isMail = to?.startsWith('mailto:');
  // Links containing an email address. No href rewriting required.
  if (isMail) {
    return (
      <DSLink
        onClick={() => {
          handleClick(to, { element_type: 'MailLink' });
        }}
        href={to}
        activeClassName={activeClassName}
        {...other}
      >
        {children}
      </DSLink>
    );
  }

  const isHash = to?.startsWith('#');
  // Links containing a hash/anchor. No href rewriting required.
  if (isHash) {
    return (
      <DSLink
        onClick={() => {
          handleClick(to, { element_type: 'HashLink' });
        }}
        href={to}
        activeClassName={activeClassName}
        {...other}
      >
        {children}
      </DSLink>
    );
  }

  /*
   * Tailor the following test to your environment.
   * This example assumes that any internal link (intended for Gatsby)
   * will start with exactly one slash, and that anything else is external.
   */
  const internal = /(^\/(?!\/))|(^(https*:\/\/(.*)\.hausgold.de))/.test(to);
  const replacePath = (_path) =>
    _path === `/` ? _path : _path.replace(/\/$|$/, `/`);

  // Use Gatsby Link (or Ds link) for internal links, and <a> with nofollow for others
  if (internal || to === undefined) {
    /**
     * For some voodoo cases the GatsbyLink does not work as it routes to selected page and immediately
     * routes back to the previous one the click was triggered from. For those cases we can use the
     * native link (styled by DS) but without prefetching logic etc. GatsbyLink offers.
     *
     * In special this problem occurs if following comes together:
     * - iPhone with Safari/Chrome or macOS with Safari is used
     * - you open the root page hausgold.de
     * - you accept all cookies or the second or third option at least
     * - you scroll down to the footer and click on e.g. "karriere"
     *
     * If you open another page like an article, the /karriere page directly or decline all cookies,
     * or the second and third at least, everything works fine just like on all other browsers / devices.
     *
     * Disable all tracking / scripts on tag manager does not fix the issue, so it does not seem to be
     * raised by a malformed third-party code.
     *
     * In theory the only downside of not using GatsbyLink is the missing state managing which we do not use
     * and that entering the linked page takes a bit longer as it was not prefetched.
     */
    if (useNativeLink) {
      const linkTo = replacePath(
        to.replace(/^https*:\/\/(www.)*hausgold\.de\//, '/')
      );
      return (
        <DSLink
          onClick={() => {
            handleClick(linkTo);
          }}
          href={linkTo}
          activeClassName={activeClassName}
          {...other}
        >
          {children}
        </DSLink>
      );
    }

    const linkTo = replacePath(
      to?.replace(/^https*:\/\/(www.)*hausgold\.de\//, '/')
    );
    return (
      <DSLink
        as={GatsbyLink}
        onClick={() => {
          handleClick(linkTo);
        }}
        to={linkTo}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        {...other}
      >
        {children}
      </DSLink>
    );
  }

  /*
   * If the `to` link is included in the Follow Links, `nofollow` should NOT
   * be used. Otherwise, `nofollow` is applied.
   */
  const isIncludedInFollowList =
    doFollow || followLinks.map((link) => link.url).includes(to);

  return (
    <OutboundLink
      rel={
        isIncludedInFollowList
          ? EXTERNAL_LINK_REL.slice(
              EXTERNAL_LINK_REL.indexOf('nofollow') + 1,
              EXTERNAL_LINK_REL.length
            )
          : EXTERNAL_LINK_REL
      }
      eventLabel={eventLabel}
      eventParams={eventParams}
      eventName={eventName}
      href={to}
      {...other}
    >
      {children}
    </OutboundLink>
  );
};

export default Link;
